import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';

/** Case-insensitive "name contains the search", true for an empty search */
export const matchesSearch = (name: string | null | undefined, search: string): boolean =>
  !search.trim() || (name ?? '').toLowerCase().includes(search.trim().toLowerCase());

type Props = {
  title: React.ReactNode;
  // Right of the search box, e.g. the section's add button
  actions?: React.ReactNode;
  searchPlaceholder?: string;
  defaultOpen?: boolean;
  // false: no search box, for a section too short to need one
  searchable?: boolean;
  // Opens the section when it turns true, e.g. to show an error that sits inside it
  openWhen?: boolean;
  // Receives the search, to show only the matching entries
  children: (search: string) => React.ReactNode;
};

/**
 * A User Attributes block that opens / closes with its arrow and filters its entries with its search box.
 * Starts closed, so the page lists the sections and the operator opens the one they need; the search box
 * stays in the header either way.
 * A closed section is hidden, not unmounted: what was typed in it (and the badges' unsaved changes) is kept.
 */
export default function UserAttributeSection({
  title,
  actions,
  searchPlaceholder = 'Search by name',
  defaultOpen = false,
  searchable = true,
  openWhen = false,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');
  // Opened by typing in the search, not by the arrow: clearing the search closes it again
  const openedBySearch = useRef(false);

  useEffect(() => {
    if (openWhen) {
      openedBySearch.current = false;
      setOpen(true);
    }
  }, [openWhen]);

  const toggle = () => {
    openedBySearch.current = false;
    setOpen(!open);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    if (value.trim() && !open) {
      openedBySearch.current = true;
      setOpen(true);
    } else if (!value.trim() && openedBySearch.current) {
      openedBySearch.current = false;
      setOpen(false);
    }
  };

  return (
    <Form.Group className="uv-section">
      <div className="d-flex justify-content-between align-items-center uv-items-header">
        <div className="uv-items-header-side">
          <Button
            variant="link"
            className="uv-section-toggle"
            onClick={toggle}
            aria-expanded={open}
            aria-label={open ? 'Close section' : 'Open section'}
          >
            <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
          </Button>
        </div>
        <Form.Label className="uv-items-title uv-section-title" onClick={toggle}>
          {title}
        </Form.Label>
        <div className="uv-items-header-side d-flex justify-content-end align-items-center uv-section-tools">
          {/* Shown on a closed section too: typing opens it on the matches, clearing closes it back */}
          {searchable && (
            <Form.Control
              size="sm"
              type="search"
              className="uv-section-search"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => handleSearch(event.target.value)}
            />
          )}
          {actions}
        </div>
      </div>
      <div hidden={!open}>{children(search)}</div>
    </Form.Group>
  );
}
